import React from "react";
import { FiEye, FiZap, FiCpu, FiHeadphones, FiBox, FiActivity, FiLayers, FiShield, FiTrendingUp, FiShoppingCart } from "react-icons/fi";
import { BsUnity, BsJoystick } from "react-icons/bs";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPalette,
  LuPercent,
  LuShield,
  LuCpu,
  LuClipboardList,
  LuSearch,
  LuCircleCheck,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const vrHeroData = {
  backgroundImage: "/images/serviceBanners/VRBanner.webp",
  whiteImage: "/images/serviceBanners/VRW.webp",
  subTitle: " Immersive VR App Experiences",
  title: (
    <>
      Step Into Immersive <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">VR App Development</span> Solutions
    </>
  ),
  description:
    "We develop immersive VR applications that deliver realistic, interactive, and scalable virtual experiences for training, simulations, entertainment, and engagement, helping businesses create powerful and engaging digital environments.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("VR Start clicked"),
  },
  secondaryButton: {
    title: "View VR Demos",
    onClick: () => console.log("VR Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "High Performance",
      desc: "Smooth and optimized VR application performance.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Responsive Design",
      desc: "Works seamlessly across VR devices and screens.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure & Reliable",
      desc: "Built with strong security and stable architecture.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Clean Code",
      desc: "Maintainable and developer-friendly VR development structure.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg",
      top: "12%",
      right: "10%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};

// 2. Stats Block (Consistent with Trending Tech vertical)
export const vrStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const vrSolutionsAndProcessData = {
  buildSubtitle: "OUR VR CAPABILITIES",
  buildTitle: "Custom VR App Development Solutions for Every Business Need",
  buildCards: [
    {
      title: "Cross-Platform VR Development",
      desc: "Build VR applications compatible across multiple devices and platforms.",
      icon: FiLayers,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise VR Solutions",
      desc: "Develop scalable and secure VR systems for complex business use cases.",
      icon: FiShield,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce VR Experiences",
      desc: "Create interactive shopping environments using virtual reality.",
      icon: FiShoppingCart,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand VR Solutions",
      desc: "Build real-time VR applications for service-based experiences.",
      icon: FiActivity,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech VR Solutions",
      desc: "Design secure and immersive financial experience systems.",
      icon: FiTrendingUp,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Custom VR Development",
      desc: "Tailored VR applications built around specific business requirements.",
      icon: LuSettings,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR VR APP DEVELOPMENT PROCESS",
  processTitle: "VR App Development Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Consultation",
      description: "Understand business goals, target audience, and VR experience requirements.",
      icon: LuSearch,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define VR features, platform support, and development roadmap.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "UI/UX Design",
      description: "Create immersive and interactive virtual reality experience designs.",
      icon: LuPalette,
    },
    {
      step: "04",
      title: "Development",
      description: "Build high-performance VR applications with scalable architecture.",
      icon: LuCode,
    },
    {
      step: "05",
      title: "Quality Testing",
      description: "Test VR interactions, performance, and device compatibility.",
      icon: LuCircleCheck,
    },
    {
      step: "06",
      title: "Deployment & Support",
      description: "Launch VR applications and provide ongoing maintenance and updates.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR VR APP DEVELOPMENT SERVICES?",
  benefitsTitle: "Why Businesses Choose VR App Development Solutions",
  benefits: [
    "Cross-platform VR",
    "Faster VR development",
    "Immersive UI design",
    "Smooth VR performance",
    "Efficient VR workflow",
    "Scalable VR solutions",
    "Cost-effective development",
  ],
  logoImage: "/images/serviceBanners/VRBanner.webp",
  whiteLogoImage: "/images/serviceBanners/VRW.webp",
};

// 4. Portfolio Data
export const vrPortfolioData = {
  subtitle: "SUCCESS STORIES",
  title: "Virtual Worlds Redefining Industry",
  buttonText: "View VR Showcase",
  buttonLink: "#",
  apps: [
    {
      title: "MedSim Alpha",
      badge: "Healthcare",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "A surgical training simulator with real-time haptic feedback and anatomy visualization.",
      downloads: "Enterprise",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1626379953822-baec19c3bbcd?auto=format&fit=crop&w=800&q=80",
    },
  ],
};

// 5. Technologies Data
export const vrTechnologiesData = {
  techTitle: "VR TOOL STACK",
  technologies: [
    { name: "Unity / Unreal", icon: "Vite" },
    { name: "OpenXR", icon: "RestAPI" },
    { name: "Oculus / Meta", icon: "Code" },
    { name: "Spatial Audio", icon: "ReactNative" },
    { name: "Blender 3D", icon: "Palette" },
    { name: "Nvidia VRWorks", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY TECHNOLOGY LEADERS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

export const vrAboutData = [
  {
    title: "VR App Development Company for Immersive Digital Experiences",
    content: (
      <>
        Businesses are increasingly investing in virtual environments to improve engagement, training, and customer interaction. A trusted{" "}
        <b className="text-text-primary font-semibold">vr app development company</b> helps organizations create immersive experiences that improve learning, visualization, and
        digital communication. Many industries struggle to deliver engaging digital experiences that hold user attention and improve participation. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, VR applications are developed around business objectives and real-world use cases to help
        organizations create meaningful and scalable virtual experiences.
      </>
    ),
  },
  {
    title: "Virtual Reality App Development for Smarter User Interaction",
    content: (
      <>
        Modern digital experiences require stronger engagement and deeper interaction than traditional applications can offer. Reliable{" "}
        <b className="text-text-primary font-semibold">virtual reality app development</b> helps businesses create interactive environments that improve training, product
        demonstrations, and customer experiences. Companies investing in <b className="text-text-primary font-semibold">immersive vr solutions</b> often improve user engagement
        because virtual environments create stronger emotional connections and practical understanding. Tycho Technologies focuses on building practical VR systems that support
        usability, business performance, and long-term digital adoption.
      </>
    ),
  },
  {
    title: "VR Game and Simulation Development for Better Experiences",
    content: (
      <>
        Virtual simulations are transforming industries such as education, healthcare, manufacturing, and real estate by improving learning and interactive experiences. Effective{" "}
        <b className="text-text-primary font-semibold">vr game and simulation development</b> helps businesses create realistic environments that improve training efficiency and
        customer engagement. Organizations looking to strengthen digital infrastructure often align immersive technologies with{" "}
        <a href="https://tychotechnologies.com/devops-cloud-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          devops consulting services
        </a>{" "}
        to improve system performance and support scalable virtual experiences.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a VR Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">vr development company in india</b> continues to grow as businesses seek advanced digital
        experiences that improve engagement and operational efficiency. Companies investing in <b className="text-text-primary font-semibold">virtual reality services india</b>{" "}
        often prioritize better interaction, realistic visualization, and scalable digital transformation. Businesses partnering with Tycho Technologies gain access to tailored VR
        frameworks designed around measurable goals and user expectations. Many organizations also strengthen immersive ecosystems through{" "}
        <a href="https://tychotechnologies.com/ar-app-development-services-india/" className="text-cyan-400 underline hover:text-cyan-300">
          ar app development company
        </a>{" "}
        solutions to create connected digital experiences across platforms.
      </>
    ),
  },
];

// 7. FAQs
export const vrDevelopmentFaqs = [
  {
    q: "What is VR app development and how is it used?",
    a: "VR app development creates immersive experiences explored via headsets. It’s used for high-stakes training, gaming, tours, and architectural previews to deliver high-presence interactivity.",
  },
  {
    q: "Do you support cross-platform VR?",
    a: "Yes, we build using industry standards like OpenXR and engines like Unity to ensure your app works across Meta Quest, HTC Vive, and Valve Index platforms.",
  },
];
